import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { egp } from '@ContentManagement/EnrollmentInvoices';
import usePlatformMode from '@Hooks/usePlatformMode';
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { GraphSlice } from '@Services/classrooms/classrooms.res.types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import DistributionDonuts from '../Components/DistributionDonuts';
import HubDateRange, { HubRange } from '../Components/HubDateRange';
import WalletStatement from '../Components/WalletStatement';
import MethodGraphSection from '../Components/MethodGraphSection';
import { RevenueSummaryCards } from '../Components/SummaryCards';
import { analyticsSeasonStart, FAMILY_COLORS, FAMILY_LABELS } from '../Constants/methods';
import { OverviewGraphRequest, OverviewRequest } from '../Services/Analytics.req';
import { FamilyKey, OverviewData } from '../Types/Analytics.types';

/**
 * The hub dashboard: platform-wide enrollment + revenue cards, the
 * admin-only wallet block, per-family mini cards, a per-family trend graph
 * and the top products by revenue.
 */
const OverviewTab: React.FC<{ instructorId?: number }> = ({ instructorId }) => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled, isRouteEnabled } =
		usePlatformMode();
	const [data, setData] = useState<OverviewData | null>(null);

	const familyEnabled = (key: FamilyKey) =>
		key === 'classrooms'
			? classroomsEnabled
			: key === 'standalone-courses'
			? standaloneEnabled
			: bundlesEnabled;

	const load = useCallback(
		async (range: HubRange) => {
			try {
				const response = await OverviewRequest({
					from: range.from,
					to: range.to,
					instructor_id: instructorId,
				});
				setData(response.data.data || null);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load the statistics overview'
				);
			}
		},
		[displaySnackbar, instructorId]
	);

	// Season by default: the reporting window opens at the most recent
	// 1st of August. Clear switches to all-time.
	const seasonRange = useMemo(() => ({ from: analyticsSeasonStart() }), []);
	const [currentRange, setCurrentRange] = useState<HubRange>(seasonRange);
	useEffect(() => {
		load(seasonRange);
	}, [load, seasonRange]);
	// The opening-balance line only makes sense when the range extends to now.
	const rangeEndsNow =
		!currentRange.to || currentRange.to.toDateString() === new Date().toDateString();

	const fetchOverviewGraph = useCallback(
		async (params: { slice: string; from: Date; to: Date }) => {
			const response = await OverviewGraphRequest({ ...params, instructor_id: instructorId });
			return response.data.data || [];
		},
		[instructorId]
	);

	const familyDistribution = (metric: 'enrollments' | 'total_revenue') =>
		(data?.families || [])
			.filter((family) => familyEnabled(family.key) || family[metric] > 0)
			.map((family) => ({
				id: 0,
				name: FAMILY_LABELS[family.key],
				value: family[metric],
			}));

	return (
		<OverviewPage>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb="1.5rem">
				<Typography variant="h6">Overview</Typography>
				<HubDateRange
					initialRange={seasonRange}
					onApply={(range) => {
						setCurrentRange(range);
						load(range);
					}}
				/>
			</Box>

			{/* The whole top section is seat value: every card values
			    currently-active enrollments, and the six method cards sum to
			    the total exactly. Money movement lives in the Wallets
			    statement below — refunds, discounts and per-product detail
			    stay on the family tabs. */}
			{data && (
				<RevenueSummaryCards
					showRefundCards={false}
					totalSubLabel="value of active enrollments"
					summary={{
						methods: data.methods,
						grand_total: data.revenue.grand_total,
						enrollments: data.enrollment_summary.purchase_methods_total,
						discounts: data.revenue.discounts,
						refunded: data.revenue.refunded,
						refund_count: 0,
						net: data.revenue.total_revenue,
					}}
				/>
			)}

			{data?.wallet && <WalletStatement statement={data.wallet} rangeEndsNow={rangeEndsNow} />}

			{data && (
				<DistributionDonuts
					enrollments={familyDistribution('enrollments')}
					revenue={familyDistribution('total_revenue')}
					enrollmentsTitle="Enrollments by product type"
					revenueTitle="Seat value by product type"
				/>
			)}

			<MethodGraphSection
				label="Enrollments per product family"
				fetchSeries={fetchOverviewGraph}
				colors={(line: GraphSlice) => FAMILY_COLORS[String(line.id)] || '#6C63FF'}
			/>

			{!!data?.top_products?.length && (
				<>
					<SectionTitle>Top products by revenue</SectionTitle>
					<TopTableBox>
						<Table sx={{ minWidth: '60rem' }}>
							<TableHead>
								<TableRow>
									<TableCell>Type</TableCell>
									<TableCell>Name</TableCell>
									<TableCell>Instructor</TableCell>
									<TableCell align="center">Enrollments</TableCell>
									<TableCell align="center">Wallet</TableCell>
									<TableCell align="center">Scratchcards</TableCell>
									<TableCell align="center">Other</TableCell>
									<TableCell align="center">Total Value</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.top_products.map((product) => {
									const route = isRouteEnabled({
										route: product.type,
										requiresClassrooms: product.type === 'classrooms',
									} as any)
										? `/dashboard/analytics/${product.type}`
										: null;
									return (
										<TableRow
											key={`${product.type}-${product.product_id}`}
											hover
											sx={{ cursor: route ? 'pointer' : 'default' }}
											onClick={() => route && history.push(route)}
										>
											<TableCell>
												<TypeChip $color={FAMILY_COLORS[product.type]}>
													{FAMILY_LABELS[product.type]}
												</TypeChip>
											</TableCell>
											<WrappedTableCell>{product.name}</WrappedTableCell>
											<WrappedTableCell>{product.instructor_name || '-'}</WrappedTableCell>
											<TableCell align="center">{product.enrollments}</TableCell>
											<TableCell align="center">{egp(product.collected)}</TableCell>
											<TableCell align="center">{egp(product.scratchcard_value)}</TableCell>
											<TableCell align="center">{egp(product.other)}</TableCell>
											<TableCell align="center">{egp(product.total_revenue)}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TopTableBox>
				</>
			)}

			{data && (
				<NotesList>
					<li>
						All values reflect currently-active enrollments — removed or refunded seats
						are excluded. Money movement is in the Wallets section.
					</li>
					{data.revenue_since && (
						<li>
							Value figures are computed from financial snapshots and begin{' '}
							{new Date(data.revenue_since).toLocaleDateString(undefined, {
								day: 'numeric',
								month: 'short',
								year: 'numeric',
							})}
							; enrollment counts cover all history.
						</li>
					)}
					<li>
						Scratchcard value is each product's list price — the actual offline sale
						price of a card is not recorded.
					</li>
					<li>
						Deactivated instructors are excluded from every figure, including their
						historical sales.
					</li>
					<li>
						Bundle value is reported on the bundle itself, not distributed to its
						member courses.
					</li>
				</NotesList>
			)}
		</OverviewPage>
	);
};

const NotesList = styled.ul`
	margin: 1.5rem 0 0;
	padding-left: 1.25rem;
	font-size: 0.8rem;
	color: rgba(0, 0, 0, 0.55);

	li {
		margin-bottom: 0.35rem;
	}
`;

const OverviewPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
	padding-bottom: 3rem;
`;

const SectionTitle = styled.div`
	font-size: 1.125rem;
	font-weight: 600;
	margin: 1rem 0;
`;


const TopTableBox = styled.div`
	background: #fff;
	border-radius: 5px;
	overflow-x: auto;
`;

const TypeChip = styled.span<{ $color?: string }>`
	display: inline-block;
	padding: 0.2rem 0.6rem;
	border-radius: 1rem;
	font-size: 0.75rem;
	font-weight: 600;
	color: ${(props) => props.$color || '#6C63FF'};
	border: 1px solid ${(props) => props.$color || '#6C63FF'};
	white-space: nowrap;
`;

export default OverviewTab;
